import { Box, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import CloseIcon from '@material-ui/icons/Close';
import Draggable from "react-draggable";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { coPiPositionDataById, navLogTargetState } from "../../store/CopilotState";
import { formatTimeZoneDate, formatTimeZoneStr } from "../../utils/FPEUtils";
import { timeZoneSelector } from "../../store/TimeZoneState";
import { OnClickAnchor } from "../../parts/OnClickAnchor";
import { useLinkableSourceList } from "../../store/AircraftSettingState";

const useStyles = makeStyles({
  navInfo: {
    display: 'flex',
    padding: '0 3px',
    fontWeight: 'bold'
  },
  navInfoLabel: {
    paddingRight: 5
  },
  tableContainer: {
    maxHeight: '100%'
  },
  tableBody: {
    backgroundColor: '#bfbfbf'
  },
  closeButton: {
    cursor: 'pointer'
  },
  navLog: {
    position: 'absolute',
    zIndex: 999,
    backgroundColor: '#FFFFFF'
  },
  navLogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: "move"
  },
  navLogBody: {
    resize: 'both',
    overflowY: 'hidden',
    overflowX: 'hidden',
    width: 1200,
    height: 240,
    padding: '3px 3px 20px 3px',
  }
});

function NavLog() {
  const classes = useStyles();
  const {t} = useTranslation();
  const timeZone = useRecoilValue(timeZoneSelector);

  const [targetAircraftId, setTargetAircraftId] = useRecoilState(navLogTargetState);
  const data = useRecoilValue(coPiPositionDataById(targetAircraftId));
  const [navLog, setNavLog] = useState([]);
  const {linkableSourceList} = useLinkableSourceList();
  useEffect(() => {
    if (data) {
      const filteredData = data.data.filter(d => d.geometry.type === 'Point' && d.properties.data_source_id === linkableSourceList[0].data_source_id).sort((a, b) => {
        if (a.properties.obs_time < b.properties.obs_time) {
          return 1;
        } else if (a.properties.obs_time > b.properties.obs_time) {
          return -1;
        } else {
          return 0;
        }
      });
      setNavLog(filteredData);
    } else {
      setNavLog([]);
    }
  }, [data, linkableSourceList]);

  const handleClose = () => {
    setTargetAircraftId('');
  }

  const windowRef = useRef(null);
  const [position, setPosition] = useState()
  const handleDragStop = (_, e) => {
    
    let {x, y} = e;
    // 上にはみ出してる場合
    if (y < 0) y = 0;
    // 右にはみ出してる場合
    if (x > windowRef.current.parentElement.clientWidth) x = windowRef.current.parentElement.clientWidth - 50;
    // 下にはみ出してる場合
    if (y > windowRef.current.parentElement.clientHeight) y = windowRef.current.parentElement.clientHeight - 50;
    // 左にはみ出してる場合
    if (x + windowRef.current.clientWidth < 0) x = -(windowRef.current.clientWidth - 50);
    
    setPosition({x:x, y:y});
  }

  return (
    <Draggable handle=".widget-header" onStop={handleDragStop} position={position}>
      <div className={clsx("widget-box", "dark-color", classes.navLog)} hidden={targetAircraftId ? false : true} ref={windowRef}>
        <div className={clsx("widget-header", classes.navLogHeader)}>
          <h4>NAV LOG</h4>
          <OnClickAnchor onClick={handleClose} className="close-anchor">
            <Tooltip title="Close" arrow >
              <CloseIcon/>
            </Tooltip>
          </OnClickAnchor>
        </div>
        <div className={classes.navLogBody}>
          <div className={classes.navInfo}>
            <div className={classes.navInfoLabel}>{t('Aircraft Code')}: </div>
            <div>{targetAircraftId}</div>
          </div>
          {navLog.length === 0 &&
            <Box>{t('Nav Log data is none.')}</Box>
          }
          {
            navLog.length > 0 &&
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>{`${t('OBSTIME')} (${formatTimeZoneStr(timeZone)})`}</TableCell>
                    <TableCell>{t('Lat')}</TableCell>
                    <TableCell>{t('Lon')}</TableCell>
                    <TableCell>{`${t('Altitude')}(ft)`}</TableCell>
                    <TableCell>{`${t('Speed')}(kt)`}</TableCell>
                    <TableCell>{t('Heading')}</TableCell>
                    <TableCell>{t('SBD IMEI')}</TableCell>
                    <TableCell>{t('Battery')}</TableCell>
                    <TableCell>{t('Alert')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className={classes.tableBody}>
                  {
                    navLog.map((row, index) => {
                      return (
                        <TableRow key={`nav-log-${index}`}>
                          <TableCell>{formatTimeZoneDate(row.properties.obs_time)}</TableCell>
                          <TableCell>{row.geometry.coordinates[1]}</TableCell>
                          <TableCell>{row.geometry.coordinates[0]}</TableCell>
                          <TableCell>{Math.round(row.properties.alt_ft * 100) / 100}</TableCell>
                          <TableCell>{row.properties.gps_spd}</TableCell>
                          <TableCell>{row.properties.heading}</TableCell>
                          <TableCell>{row.properties.sbd_imei}</TableCell>
                          <TableCell>{row.properties.battery}</TableCell>
                          <TableCell>
                            {row.properties.approach_alert && t('approach')}
                            {(row.properties.approach_alert && row.properties.deviation_alert) && ', '}
                            {row.properties.deviation_alert && t('deviation')}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  }
                </TableBody>
              </Table>
            </TableContainer>
          }
        </div>
      </div>
    </Draggable>
  )
}

export default NavLog;